<template>
  <div v-if="show" class="modal-overlay" @click.self="closeModal">
    <div class="modal-container">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <i class="fas fa-calendar-times"></i>
          <div class="header-text">
            <h2>Reporter l'Intervention</h2>
            <p>Choisissez le motif du report</p>
          </div>
        </div>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <!-- Informations Intervention -->
        <div class="info-section">
          <h3><i class="fas fa-info-circle"></i> Intervention</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="label">Client :</span>
              <span class="value">{{ intervention.client?.nom || 'N/A' }}</span>
            </div>
            <div class="info-item">
              <span class="label">Date :</span>
              <span class="value">{{ formatDate(intervention.dateRdv) }}</span>
            </div>
            <div class="info-item">
              <span class="label">Horaire :</span>
              <span class="value">{{ intervention.heureDebut }} - {{ intervention.heureFin }}</span>
            </div>
          </div>
        </div>

        <!-- Motif automatique basé sur la date -->
        <div class="motif-section">
          <h3><i class="fas fa-exclamation-triangle"></i> Motif du Report</h3>
          <div class="motif-display">
            <div class="motif-info">
              <div class="motif-icon">
                <i :class="motifIcon"></i>
              </div>
              <div class="motif-content">
                <h4>{{ motifTitle }}</h4>
                <p>{{ motifDescription }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Champ date optionnel -->
        <div class="date-section">
          <h3><i class="fas fa-calendar-plus"></i> Nouvelle Date (Optionnel)</h3>
          <p class="date-help">Si vous proposez une nouvelle date, le motif sera "Technicien Indisponible". Sinon, ce sera "Changer Technicien".</p>
          <div class="date-inputs">
            <div class="input-group">
              <label>Nouvelle Date</label>
              <input 
                type="date" 
                v-model="nouvelleDate"
                :min="minDate"
                class="date-input"
                placeholder="Laisser vide pour changer de technicien"
              >
            </div>
            <div class="input-group" v-if="nouvelleDate">
              <label>Heure Début</label>
              <input 
                type="time" 
                v-model="nouvelleHeureDebut"
                class="time-input"
              >
            </div>
            <div class="input-group" v-if="nouvelleDate">
              <label>Heure Fin</label>
              <input 
                type="time" 
                v-model="nouvelleHeureFin"
                class="time-input"
              >
            </div>
          </div>
        </div>

        <!-- Commentaires -->
        <div class="comment-section">
          <h3><i class="fas fa-comment"></i> Commentaires</h3>
          <textarea 
            v-model="commentaires"
            placeholder="Ajoutez des détails sur le motif du report..."
            class="comment-textarea"
            rows="4"
          ></textarea>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <button @click="closeModal" class="btn-cancel">
          <i class="fas fa-times"></i>
          Annuler
        </button>
        <button 
          @click="submitReport" 
          class="btn-submit"
          :disabled="!canSubmit"
        >
          <i class="fas fa-paper-plane"></i>
          Reporter l'Intervention
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import axios from 'axios'
import { useToast } from 'vue-toastification'

export default {
  name: 'ReportInterventionModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    intervention: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'reported'],
  setup(props, { emit }) {
    const toast = useToast()

    // États
    const nouvelleDate = ref('')
    const nouvelleHeureDebut = ref('')
    const nouvelleHeureFin = ref('')
    const commentaires = ref('')

    // Date minimum (aujourd'hui)
    const minDate = computed(() => {
      const today = new Date()
      return today.toISOString().split('T')[0]
    })

    // Motif automatique basé sur la date
    const selectedMotif = computed(() => {
      return nouvelleDate.value ? 'technicien_indisponible' : 'changer_technicien'
    })

    // Informations du motif pour l'affichage
    const motifIcon = computed(() => {
      return nouvelleDate.value ? 'fas fa-user-times' : 'fas fa-user-edit'
    })

    const motifTitle = computed(() => {
      return nouvelleDate.value ? 'Technicien Indisponible' : 'Changer Technicien'
    })

    const motifDescription = computed(() => {
      return nouvelleDate.value 
        ? 'Le technicien ne peut pas effectuer l\'intervention à la date prévue'
        : 'Libérer le technicien actuel et affecter un autre technicien à cette date'
    })

    // Validation
    const canSubmit = computed(() => {
      if (!commentaires.value.trim()) return false
      
      if (selectedMotif.value === 'technicien_indisponible') {
        return nouvelleDate.value && nouvelleHeureDebut.value && nouvelleHeureFin.value
      }
      
      return true
    })

    // Réinitialiser les heures quand on vide la date
    watch(nouvelleDate, (newDate) => {
      if (!newDate) {
        nouvelleHeureDebut.value = ''
        nouvelleHeureFin.value = ''
      }
    })

    // Formatage de date
    const formatDate = (date) => {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    // Soumettre le report
    const submitReport = async () => {
      try {
        const token = localStorage.getItem('token')
        
        const reportData = {
          motif: selectedMotif.value,
          commentaires: commentaires.value
        }

        // Ajouter la nouvelle date si technicien indisponible
        if (selectedMotif.value === 'technicien_indisponible') {
          reportData.nouvelleDate = nouvelleDate.value
          reportData.nouvelleHeureDebut = nouvelleHeureDebut.value
          reportData.nouvelleHeureFin = nouvelleHeureFin.value
        }

        await axios.post(
          `/api/rdv/${props.intervention._id}/reporter`,
          reportData,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        )

        toast.success('✅ Intervention reportée avec succès')
        emit('reported')
        closeModal()
      } catch (error) {
        console.error('Erreur lors du report:', error)
        toast.error('❌ Erreur lors du report de l\'intervention')
      }
    }

    // Fermer le modal
    const closeModal = () => {
      nouvelleDate.value = ''
      nouvelleHeureDebut.value = ''
      nouvelleHeureFin.value = ''
      commentaires.value = ''
      emit('close')
    }

    // Réinitialiser quand le modal s'ouvre
    watch(() => props.show, (newVal) => {
      if (newVal) {
        nouvelleDate.value = ''
        nouvelleHeureDebut.value = ''
        nouvelleHeureFin.value = ''
        commentaires.value = ''
      }
    })

    return {
      selectedMotif,
      nouvelleDate,
      nouvelleHeureDebut,
      nouvelleHeureFin,
      commentaires,
      minDate,
      canSubmit,
      motifIcon,
      motifTitle,
      motifDescription,
      formatDate,
      submitReport,
      closeModal
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.modal-container {
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* Header */
.modal-header {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  padding: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 20px 20px 0 0;
  position: relative;
  overflow: hidden;
}

.modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255,255,255,0.1), transparent);
  pointer-events: none;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 1;
}

.header-content > i {
  font-size: 2rem;
  animation: iconPulse 2s ease-in-out infinite;
}

.header-text h2 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.header-text p {
  margin: 0.25rem 0 0 0;
  opacity: 0.9;
  font-size: 0.9rem;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

/* Body */
.modal-body {
  padding: 2rem;
}

/* Info Section */
.info-section {
  background: linear-gradient(135deg, rgba(0, 70, 255, 0.05), rgba(0, 27, 183, 0.05));
  border: 1px solid rgba(0, 70, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.info-section h3 {
  margin: 0 0 1rem 0;
  color: var(--primary-blue);
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-item .label {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
}

.info-item .value {
  font-size: 1rem;
  color: #212529;
  font-weight: 600;
}

/* Motif Section */
.motif-section {
  margin-bottom: 1.5rem;
}

.motif-section h3 {
  margin: 0 0 1rem 0;
  color: #212529;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.motif-display {
  background: linear-gradient(135deg, rgba(0, 70, 255, 0.1), rgba(0, 27, 183, 0.05));
  border: 2px solid rgba(0, 70, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
}

.motif-info {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.motif-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.motif-content h4 {
  margin: 0 0 0.5rem 0;
  color: #212529;
  font-size: 1rem;
  font-weight: 600;
}

.motif-content p {
  margin: 0;
  color: #6c757d;
  font-size: 0.85rem;
  line-height: 1.4;
}

/* Date Section */
.date-section {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(5, 150, 105, 0.05));
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.date-section h3 {
  margin: 0 0 0.5rem 0;
  color: #10b981;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.date-help {
  margin: 0 0 1rem 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
  font-style: italic;
}

.date-inputs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-group label {
  font-size: 0.85rem;
  color: #6c757d;
  font-weight: 500;
}

.date-input,
.time-input {
  padding: 0.75rem;
  border: 2px solid rgba(226, 232, 240, 0.8);
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  background: white;
}

.date-input:focus,
.time-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 70, 255, 0.1);
  transform: translateY(-2px);
}

/* Info Message */
.info-message {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(37, 99, 235, 0.05));
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.info-message i {
  font-size: 1.5rem;
  color: #3b82f6;
  flex-shrink: 0;
}

.info-message p {
  margin: 0;
  color: #1e40af;
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Comment Section */
.comment-section h3 {
  margin: 0 0 1rem 0;
  color: #212529;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.comment-textarea {
  width: 100%;
  padding: 1rem;
  border: 2px solid rgba(226, 232, 240, 0.8);
  border-radius: 12px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
}

.comment-textarea:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 70, 255, 0.1);
}

/* Footer */
.modal-footer {
  padding: 1.5rem 2rem;
  background: rgba(248, 250, 252, 0.8);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(226, 232, 240, 0.8);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  border-radius: 0 0 20px 20px;
}

.btn-cancel,
.btn-submit {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.btn-cancel {
  background: linear-gradient(135deg, #6c757d, #5a6268);
  color: white;
}

.btn-cancel:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(108, 117, 125, 0.3);
}

.btn-submit {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 70, 255, 0.3);
}

.btn-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    max-height: 95vh;
  }

  .modal-header {
    padding: 1.5rem;
  }

  .header-text h2 {
    font-size: 1.25rem;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .motif-options,
  .date-inputs {
    grid-template-columns: 1fr;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
